import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Button, TextField, Paper, Typography, Container, Tooltip, Modal,
    MenuItem, Box
} from "@mui/material";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const AddRoutes = () => {
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const navigate = useNavigate();
    const location = useLocation();
    const { encodedFormId } = location.state || {};
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [branchId, setBranchId] = useState("");
    const [branch, setBranch] = useState([]);
    const [routeName, setRouteName] = useState("");
    const [distance, setDistance] = useState("");
    const [routeCharge, setRouteCharge] = useState("");
    const [description, setDescription] = useState("");

    const [routeNameError, setRouteNameError] = useState("");
    const [distanceError, setDistanceError] = useState("");
    const [routeChargeError, setRouteChargeError] = useState("");

    const handleReset = () => {
        setDescription("");
        setRouteName("");
        setDistance("");
        setRouteCharge("");
        setRouteNameError("");
        setDistanceError("");
        setRouteChargeError("");
    };


    const validateForm = () => {
        let isValid = true;
        setRouteNameError("");
        setDistanceError("");
        setRouteChargeError("");

        if (!routeName.trim()) {
            setRouteNameError("Route name is required");
            isValid = false;
        } else if (routeName.trim().length > 100) {
            setRouteNameError("Route name must be 100 characters or less");
            isValid = false;
        }

        if (!distance) {
            setDistanceError("Distance is required");
            isValid = false;
        } else if (parseFloat(distance) <= 0 || isNaN(parseFloat(distance))) {
            setDistanceError("Distance must be a positive number");
            isValid = false;
        }

        if (!routeCharge) {
            setRouteChargeError("Route charge is required");
            isValid = false;
        } else if (parseFloat(routeCharge) <= 0 || isNaN(parseFloat(routeCharge))) {
            setRouteChargeError("Route charge must be a positive number");
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/Transportation/Route`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                },
                body: JSON.stringify({
                    branchId: parseInt(branchId),
                    routeName: routeName.trim(),
                    distance: parseFloat(distance),
                    routeCharge: parseFloat(routeCharge),
                    description: description.trim(),
                }),
            });

            if (response.ok) {
                setShowSuccessModal(true);

            } else {
                const errorData = await response.json();
                console.error("Failed to add route:", errorData);
                // alert("Failed to add route");
                setSnackbarMessage('Failed to add route');
                setOpenSnackbar(true);
                setSnackbarSeverity("error");
            }
        } catch (error) {
            console.error("Error adding route:", error);
            // alert("An error occurred while adding the route");
            setSnackbarMessage('An error occurred while adding the route');
            setOpenSnackbar(true);
            setSnackbarSeverity("error");
        }
    };

    const fetchBranch = async () => {
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;
            const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: sessionStorage.getItem("token"),
                },
            });

            if (response.ok) {
                const responseData = await response.json();
                setBranch(responseData);
                if (responseData.length > 0) {
                    setBranchId(String(responseData[0].branchId));
                }
            } else {
                console.error("Failed to fetch branches:", response.status);
            }
        } catch (error) {
            console.error("Error fetching branches:", error);
        }
    };

    useEffect(() => {
        fetchBranch();
    }, []);

    return (
        <Container>

            <Box
                sx={{
                    backgroundColor: '#0B1F40', // dark navy blue
                    padding: '12px 24px',
                    boxShadow: 3,
                    borderRadius: '4px 4px 0 0',
                    margin: '2px auto',
                    maxWidth: 700,
                }}
            >
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
                    Add New Routes
                </Typography> </Box>


            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 700,
                    mx: "auto",
                    mt: 1,
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                }}
            >

                <form onSubmit={handleSubmit}>

                    {/* Branch */}
                    <div>
                        <Typography variant="subtitle1" gutterBottom>
                            Branch Name <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            value={branchId}
                            disabled={branch.length <= 1}
                            onChange={(e) => setBranchId(e.target.value)}
                            sx={{ backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                        >
                            <MenuItem value="" disabled>-- Select Branch --</MenuItem>
                            {branch.map((b) => (
                                <MenuItem key={b.branchId} value={String(b.branchId)}>
                                    {b.branchName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </div>

                    {/* Route Name */}
                    <div>
                        <Typography variant="subtitle1" gutterBottom>
                            Route Name <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={routeName}
                            onChange={(e) => setRouteName(e.target.value)}
                            placeholder="Enter Route Name"
                            inputProps={{ maxLength: 100 }}
                            error={!!routeNameError}
                            helperText={routeNameError}
                            sx={{ backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                        />
                    </div>

                    {/* Distance */}
                    <div>
                        <Typography variant="subtitle1" gutterBottom>
                            Distance (km) <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={distance}
                            onChange={(e) => setDistance(e.target.value)}
                            placeholder="Enter Distance"
                            inputProps={{ step: "0.01", min: "0" }}
                            error={!!distanceError}
                            helperText={distanceError}
                            sx={{ backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                        />
                    </div>

                    {/* Route Charge */}
                    <div>
                        <Typography variant="subtitle1" gutterBottom>
                            Route Charge (₹) <span style={{ color: "red" }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            value={routeCharge}
                            onChange={(e) => setRouteCharge(e.target.value)}
                            placeholder="Enter Route Charge"
                            inputProps={{ step: "0.01", min: "0" }}
                            error={!!routeChargeError}
                            helperText={routeChargeError}
                            sx={{ backgroundColor: "#f9f9f9", borderRadius: "4px" }}
                        />
                    </div>

                    {/* Description */}
                    <TextField
                        fullWidth
                        label="Description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        margin="normal"
                        multiline
                        rows={4}
                        variant="outlined"
                    />


                    {/* Buttons */}
                    <Box mt={2} display="flex" gap={2}>
                        <Tooltip title={(!routeName || !distance || !routeCharge) && "Please fill required fields"} arrow><span>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"

                                disabled={!routeName || !distance || !routeCharge}
                            >
                                SAVE
                            </Button></span></Tooltip>
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => navigate(-1)}
                        >
                            CANCEL
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleReset}
                        >
                            RESET
                        </Button>
                    </Box>
                </form>
            </Paper>
            <Modal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
                aria-labelledby="success-modal-title"
                aria-describedby="success-modal-description"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        overflow: 'hidden',
                    }}
                >
                    {/* Header */}
                    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
                        <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
                            Route Added ✅
                        </Typography>
                    </Box>

                    {/* Content */}
                    <Box sx={{ padding: '20px' }}>
                        <Typography id="success-modal-description" sx={{ mb: 2 }}>
                            The Route has been added successfully.
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    navigate(-1); // go back after closing modal
                                }}
                                variant="contained"
                                color="primary"
                            >
                                OK
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default AddRoutes;
